import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Bed, Ruler, ArrowRight } from "lucide-react";
import { api, fmtEUR } from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function Fleet() {
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState("Alle");
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Unsere Fahrzeugflotte · Wohnmobile & Kastenwagen mieten | Caravan Reisen",
    description: "Entdecke die komplette Flotte von Caravan Reisen: Kastenwagen ab 75 €/Tag, Alkoven für Familien und Teilintegrierte mit Automatik. Alle Fahrzeuge geprüft, gepflegt und rundum versichert.",
    path: "/fahrzeuge",
  });

  useEffect(() => {
    api.get("/vehicles")
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["Alle", ...new Set(vehicles.map((v) => v.category))];
  const filtered = filter === "Alle" ? vehicles : vehicles.filter((v) => v.category === filter);

  return (
    <div className="pt-28 pb-16 max-w-7xl mx-auto px-6 lg:px-10" data-testid="fleet-page">
      <div className="max-w-3xl mb-12">
        <span className="chip">Fahrzeugflotte</span>
        <h1 className="section-title mt-4 text-5xl md:text-6xl">Finde dein Reisemobil.</h1>
        <p className="mt-4 text-lg text-[#1B2A22]/70 leading-relaxed">
          Unsere Flotte ist sorgfältig gepflegt und regelmäßig gewartet. Wähle dein passendes Fahrzeug und prüfe live die Verfügbarkeit.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="fleet-filters">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c}`}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
              filter === c
                ? "bg-[#1B2A22] text-white border-[#1B2A22]"
                : "bg-white text-[#1B2A22] border-[#D8D6D0] hover:border-[#1B2A22]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-24 text-center text-[#1B2A22]/60">Lade Fahrzeuge...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((v) => (
            <Link
              key={v.id}
              to={`/fahrzeuge/${v.id}`}
              className="card-vehicle group animate-fade-up"
              data-testid={`vehicle-card-${v.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#E8E6E1]">
                <img src={v.images?.[0]} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-[#1B2A22]/60">{v.category}</span>
                  <span className="text-[#C05C42] text-sm font-semibold">{fmtEUR(v.price_per_day)}<span className="text-xs font-normal text-[#1B2A22]/60">/Tag</span></span>
                </div>
                <h3 className="font-display font-semibold text-xl">{v.name}</h3>
                <div className="flex gap-4 mt-4 text-xs text-[#1B2A22]/70">
                  <span className="flex items-center gap-1.5"><Users size={14}/>{v.seats}</span>
                  <span className="flex items-center gap-1.5"><Bed size={14}/>{v.beds}</span>
                  <span className="flex items-center gap-1.5"><Ruler size={14}/>{v.length_m}m</span>
                </div>
                <div className="mt-5 flex items-center gap-1 text-sm font-medium text-[#C05C42]">
                  Details ansehen <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
